import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, ShieldCheck, UserCheck } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useCan } from '@/hooks/useCan'
import { formatDateTime } from '@/lib/dateUtils'
import { AnalyticsStatusIndicator } from '@/lib/utils/analytics-status'
import { estimateRange } from '@/lib/utils/scan-estimate'
import { MIN_REASON_LENGTH } from '@/lib/utils/scan-status-change'
import { requestApprovalsApi, type RecoveryScanCategory, type RequestApproval } from '../api'
import { requestApprovalKeys } from '../hooks'

/**
 * The scan results for a recovery request that asked to be scanned first, and step 1 of approving
 * it: accepting them.
 *
 * Andrew, 2026-10-06: "The approval screen will need an option to show the scan results if a scan
 * was requested and will need approval before the retrieval can be approved."
 *
 * Two steps, two permissions. Accepting the results here is a security judgement
 * (scan-status.approve); approving the retrieval afterwards is the recovery approver's
 * (recovery.approve). When anything red, failed or unscanned is accepted it needs a reason, and
 * the retrieval must then be approved by a different person - the server and the approve
 * procedure both hold that line; this screen only says so before anyone finds out the hard way.
 *
 * Rules and reasons: backend/src/lib/recovery-scan.js.
 */
const ORDER: RecoveryScanCategory[] = ['detections', 'failed', 'not_scanned', 'running', 'indicators', 'clear']
const TONE: Record<RecoveryScanCategory, string> = {
    detections: 'text-red-600 dark:text-red-400',
    failed: 'text-amber-600 dark:text-amber-400',
    not_scanned: 'text-slate-600 dark:text-slate-300',
    running: 'text-slate-600 dark:text-slate-300',
    indicators: 'text-amber-600 dark:text-amber-400',
    clear: 'text-green-700 dark:text-green-400',
}

interface Props {
    request: RequestApproval | null
    onOpenChange: (open: boolean) => void
}

export function RecoveryScanDialog({ request, onOpenChange }: Props) {
    const { t, i18n } = useTranslation()
    const queryClient = useQueryClient()
    const mayAccept = useCan('scan-status.approve')
    const [reason, setReason] = useState('')
    const requestId = request?.requestID || null

    const summary = useQuery({
        queryKey: [...requestApprovalKeys.all, 'scan', requestId],
        queryFn: () => requestApprovalsApi.scanSummary(requestId as string).then((r) => r.data.data),
        enabled: !!requestId,
        // Results arrive from the scanner, not from anything this person does.
        refetchInterval: (query) => ((query.state.data?.counts.running ?? 0) > 0 ? 30_000 : false),
    })
    const accept = useMutation({
        mutationFn: (withoutUnsent: boolean) => requestApprovalsApi.acceptScan(requestId as string, { reason: reason.trim() || undefined, withoutUnsent }),
        onSuccess: () => {
            setReason('')
            queryClient.invalidateQueries({ queryKey: requestApprovalKeys.all })
        },
    })

    const data = summary.data
    const when = (d: string | null | undefined) => (d ? formatDateTime(d, i18n.language) : '')
    const failure = accept.error as any
    const failureText: string | null = failure
        ? String(t(`requestApprovals.scan.error.${failure?.response?.data?.code}`, { defaultValue: failure?.response?.data?.message || t('requestApprovals.scan.error.generic') }))
        : null
    const remaining = data?.remaining ? estimateRange(data.remaining, t) : null
    // Giving up on unsent scans leaves versions unscanned, which is a risk like any other.
    const onlyUnsentRunning = !!data && data.counts.running > 0 && data.notSent === data.counts.running
    const needsReason = !!data && (data.needsReason || onlyUnsentRunning)
    const tooShort = needsReason && reason.trim().length < MIN_REASON_LENGTH

    return (
        <Dialog open={!!request} onOpenChange={(open) => { if (!open) { setReason(''); accept.reset() } onOpenChange(open) }}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ShieldCheck className="h-5 w-5 text-muted-foreground" />
                        {t('requestApprovals.scan.title')}
                    </DialogTitle>
                    <DialogDescription>
                        {request?.objectID}{request?.objectDescription ? ` — ${request.objectDescription}` : ''}
                    </DialogDescription>
                </DialogHeader>

                {summary.isLoading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
                {summary.isError && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{t('requestApprovals.scan.error.generic')}</p>}

                {data && (
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">{t('requestApprovals.scan.intro')}</p>

                        {/* The picture: what the scans found, by what it means for this decision. */}
                        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                            {ORDER.map((c) => (
                                <div key={c} className="rounded-lg border px-3 py-2">
                                    <div className={`text-2xl font-bold tabular-nums leading-none ${data.counts[c] > 0 ? TONE[c] : 'text-muted-foreground/50'}`}>{data.counts[c]}</div>
                                    <div className="text-[11px] text-muted-foreground mt-1 leading-tight">{t(`requestApprovals.scan.category.${c}`)}</div>
                                </div>
                            ))}
                        </div>
                        {data.counts.running > 0 && (
                            <p className="flex items-center gap-2 text-sm" role="status">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {remaining ? t('requestApprovals.scan.remaining', { range: remaining }) : t('requestApprovals.scan.category.running')}
                            </p>
                        )}

                        <div className="rounded-lg border overflow-x-auto">
                            <table className="w-full text-sm">
                                <tbody>
                                    {[...data.items].sort((a, b) => ORDER.indexOf(a.category) - ORDER.indexOf(b.category)).map((item) => (
                                        <tr key={item.versionId} className="border-b last:border-b-0">
                                            <td className="px-3 py-2 align-top whitespace-nowrap">
                                                {/* The object's own page has the version's full scan report. */}
                                                <Link className="font-medium underline-offset-2 hover:underline" to={`/objects/${encodeURIComponent(item.objectId)}`}>{item.objectId}</Link>
                                            </td>
                                            <td className="px-3 py-2 align-top">
                                                <div className="text-xs font-mono break-all">{item.objectName}</div>
                                                <div className="text-xs text-muted-foreground">{[item.versionType, when(item.versionDatetime)].filter(Boolean).join(' · ')}</div>
                                            </td>
                                            <td className="px-3 py-2 align-top whitespace-nowrap">
                                                <span className="inline-flex items-center gap-1.5">
                                                    {item.category === 'running'
                                                        ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                                        : <AnalyticsStatusIndicator status={item.status} size="h-4 w-4" showIcon />}
                                                    <span className={`text-xs font-medium ${TONE[item.category]}`}>{t(`requestApprovals.scan.category.${item.category}`)}</span>
                                                    {item.changedByReview && <span title={t('objects.statusChange.markerChanged')}><UserCheck className="h-3.5 w-3.5 text-slate-600 dark:text-slate-300" /></span>}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {data.review.accepted ? (
                            <div className="rounded-lg border border-slate-400 bg-slate-50 p-3 text-sm dark:border-slate-600 dark:bg-slate-900/40">
                                <div className="font-semibold">{t('requestApprovals.scan.accepted', { who: data.review.acceptedBy?.name || t('objects.statusChange.someone'), when: when(data.review.acceptedAt) })}</div>
                                {data.review.acceptReason && <p className="mt-1 text-xs whitespace-pre-wrap break-words">{data.review.acceptReason}</p>}
                                {data.review.needsSecondPerson && <p className="mt-1 text-xs font-medium">{t('requestApprovals.scan.secondPerson')}</p>}
                            </div>
                        ) : mayAccept && (data.canAccept || onlyUnsentRunning) ? (
                            <div className="rounded-lg border p-3 space-y-2">
                                <div className="text-sm font-semibold">{t('requestApprovals.scan.accept')}</div>
                                <p className="text-xs text-muted-foreground">
                                    {t(onlyUnsentRunning ? 'requestApprovals.scan.helpUnsent' : needsReason ? 'requestApprovals.scan.helpRisk' : 'requestApprovals.scan.helpClear', { n: data.notSent })}
                                </p>
                                {needsReason && (
                                    <>
                                        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={1000}
                                            aria-label={t('requestApprovals.scan.reasonLabel')} placeholder={t('requestApprovals.scan.reasonLabel')} />
                                        <p className="text-[11px] text-muted-foreground">{t('objects.statusChange.form.minimum', { n: MIN_REASON_LENGTH })}</p>
                                    </>
                                )}
                                <p className="text-[11px] text-muted-foreground">{t('requestApprovals.scan.limits')}</p>
                                {failureText && <p role="alert" className="text-xs font-medium text-red-600 dark:text-red-400">{failureText}</p>}
                                <div className="flex justify-end">
                                    <Button disabled={tooShort || accept.isPending} onClick={() => accept.mutate(onlyUnsentRunning)}>
                                        {t(onlyUnsentRunning ? 'requestApprovals.scan.acceptWithout' : 'requestApprovals.scan.accept')}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                {t(data.counts.running > 0 ? 'requestApprovals.scan.waitForScans' : 'requestApprovals.scan.needsAccepter')}
                            </p>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}
